import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Bot, Power, Lock, AlertTriangle, Home } from 'lucide-react';
import toast from 'react-hot-toast';

type TradingBot = {
  id: string;
  name: string;
  description: string;
  risk_level: 'low' | 'medium' | 'high';
  pnl_percentage: number;
  running_days: number;
  webhook_url: string;
  is_active: boolean;
  created_at: string;
};

function TradingBots() {
  const [bots, setBots] = useState<TradingBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectingBot, setSelectingBot] = useState(false);
  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadBots();
      loadSelectedBots();
    }
  }, [user]);

  const getUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();
        
        setIsAdmin(profile?.is_admin || false);
      }
    } catch (error) {
      console.error('Error checking user status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedBots = async () => {
    if (!user?.id) return;

    try {
      // Only get currently selected bots
      const { data, error } = await supabase
        .from('user_selections')
        .select('bot_id')
        .eq('user_id', user.id)
        .eq('is_selected', true);

      if (error) throw error;
      setSelectedBots(data?.map(selection => selection.bot_id) || []);
    } catch (error) {
      console.error('Error loading selected bots:', error);
    }
  };

  const loadBots = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('trading_bots')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setBots(data || []);
    } catch (error: any) {
      toast.error('Failed to load trading bots');
      console.error('Error:', error.message);
    }
  };

  const handleToggleStatus = async (bot: TradingBot) => {
    if (!isAdmin) return;

    try {
      const { error } = await supabase
        .from('trading_bots')
        .update({ 
          is_active: !bot.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', bot.id);

      if (error) throw error;
      
      toast.success(`Bot ${bot.is_active ? 'deactivated' : 'activated'} successfully`);
      loadBots();
    } catch (error: any) {
      toast.error('Failed to update bot status');
      console.error('Error:', error.message);
    }
  };

  const handleSelectBot = async (botId: string) => {
    if (selectingBot || !user?.id) {
      if (!user?.id) {
        toast.error('Please log in to select bots');
        navigate('/login');
      }
      return;
    }
    
    setSelectingBot(true);

    try {
      // First check if selection exists
      const { data: existingSelection, error: checkError } = await supabase
        .from('user_selections')
        .select('*')
        .eq('user_id', user.id)
        .eq('bot_id', botId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingSelection) {
        // Update existing selection
        const { error: updateError } = await supabase
          .from('user_selections')
          .update({ 
            is_selected: true,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('bot_id', botId);

        if (updateError) throw updateError;
      } else {
        // Create new selection
        const { error: insertError } = await supabase
          .from('user_selections')
          .insert([{ 
            user_id: user.id, 
            bot_id: botId,
            is_selected: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (insertError) throw insertError;
      }

      // Create or update bot status
      const { error: statusError } = await supabase
        .from('user_bot_status')
        .upsert([{
          user_id: user.id,
          bot_id: botId,
          is_selected: true,
          is_trading: false,
          updated_at: new Date().toISOString()
        }], {
          onConflict: 'user_id,bot_id'
        });

      if (statusError) throw statusError;

      setSelectedBots(prev => [...prev, botId]);
      toast.success('Bot selected successfully');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error selecting bot:', error);
      toast.error('Failed to select bot');
    } finally {
      setSelectingBot(false);
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low':
        return 'bg-green-500/10 border-green-500/20';
      case 'medium':
        return 'bg-yellow-500/10 border-yellow-500/20';
      case 'high':
        return 'bg-red-500/10 border-red-500/20';
      default:
        return 'bg-gray-500/10 border-gray-500/20';
    }
  };

  const getRiskIconColor = (risk: string) => {
    switch (risk) {
      case 'low':
        return 'text-green-400';
      case 'medium':
        return 'text-yellow-400';
      case 'high':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Trading Bots</h1>
        {user ? (
          <Link
            to="/dashboard"
            className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <Home className="h-5 w-5 mr-2" />
            <span>My Homepage</span>
          </Link>
        ) : (
          <Link
            to="/login"
            className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <Lock className="h-5 w-5 mr-2" />
            <span>Log in to Start Trading</span>
          </Link>
        )}
      </div>

      {!user ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <Lock className="h-16 w-16 text-cyan-500 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Login Required</h2>
          <p className="text-gray-300 mb-8 max-w-md">
            Please log in to view and select trading bots. Create an account to get started with automated trading.
          </p>
          <div className="space-x-4">
            <Link
              to="/login"
              className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
            >
              Log In
            </Link>
            <Link
              to="/register"
              className="px-6 py-3 border border-cyan-500 text-cyan-500 hover:bg-cyan-500/10 rounded-md transition-colors"
            >
              Create Account
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bots.map((bot) => (
            <div
              key={bot.id}
              className={`p-6 rounded-lg border ${getRiskColor(bot.risk_level)} backdrop-blur-sm ${
                !bot.is_active ? 'opacity-75' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Bot className={`h-8 w-8 ${getRiskIconColor(bot.risk_level)}`} />
                  <div>
                    <h3 className="text-lg font-semibold text-white">{bot.name}</h3>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-300 capitalize">{bot.risk_level} Risk</p>
                      <span className={`text-sm px-2 py-0.5 rounded-full ${
                        bot.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {bot.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => handleToggleStatus(bot)}
                    className={`p-2 rounded-full transition-colors ${
                      bot.is_active
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                    }`}
                    title={bot.is_active ? 'Deactivate Bot' : 'Activate Bot'}
                  >
                    <Power className="h-5 w-5" />
                  </button>
                )}
              </div>

              <p className="text-gray-300 text-sm mb-4">{bot.description}</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/20 rounded-lg p-3">
                  <p className="text-sm text-gray-400">PnL</p>
                  <p className={`text-xl font-bold ${bot.pnl_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {bot.pnl_percentage >= 0 ? '+' : ''}{bot.pnl_percentage}%
                  </p>
                </div>
                <div className="bg-black/20 rounded-lg p-3">
                  <p className="text-sm text-gray-400">Running</p>
                  <p className="text-xl font-bold text-cyan-400">{bot.running_days}d</p>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => handleSelectBot(bot.id)}
                  disabled={selectingBot || !bot.is_active}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    !bot.is_active
                      ? 'bg-gray-500/50 text-gray-400 cursor-not-allowed'
                      : selectedBots.includes(bot.id)
                      ? 'bg-green-500/50 text-white cursor-not-allowed'
                      : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                  }`}
                  title={
                    !bot.is_active 
                      ? 'Bot is currently inactive'
                      : selectedBots.includes(bot.id)
                      ? 'Bot already selected'
                      : 'Select Bot'
                  }
                >
                  {selectedBots.includes(bot.id) ? 'Selected' : 'Select Bot'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TradingBots;
